import React, { useState, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useSystemDate } from '../hooks/useSystemDate';

export const LeaveManagementView = ({ handleItemSelect }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const systemDate = useSystemDate();
  
  const employees = useLiveQuery(() => 
    activeCompany ? db.employees.where('companyId').equals(activeCompany.id!).and(e => e.status === 'Active').toArray() : []
  , [activeCompany]);

  const leaves = useLiveQuery(() => 
    activeCompany ? db.employeeLeaves.where('companyId').equals(activeCompany.id!).toArray() : []
  , [activeCompany]);

  const [activeTab, setActiveTab] = useState<'apply' | 'history' | 'balances'>('history');
  
  // Apply Leave State
  const [selectedEmpId, setSelectedEmpId] = useState<number | ''>('');
  const [leaveType, setLeaveType] = useState('Casual');
  const [startDate, setStartDate] = useState(systemDate);
  const [endDate, setEndDate] = useState(systemDate);
  const [reason, setReason] = useState('');
  
  // Review Leave State
  const scrollRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const pendingLeaves = (leaves || []).filter(l => l.status === 'Pending').reverse();
  const allHistory = (leaves || []).reverse();
  
  const flatItems = activeTab === 'history' ? allHistory : [];
  
  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => {});

  const handleApplyLeave = async () => {
    if (!selectedEmpId || !startDate || !endDate || !reason.trim() || !activeCompany) {
      alert('Please fill all required fields');
      return;
    }
    
    if (startDate > endDate) {
      alert('End date cannot be before start date');
      return;
    }

    try {
      await db.employeeLeaves.add({
        companyId: activeCompany.id!,
        employeeId: Number(selectedEmpId),
        type: leaveType as 'Sick' | 'Casual' | 'Earned' | 'Unpaid',
        startDate,
        endDate,
        reason,
        status: 'Pending',
        appliedOn: new Date().toISOString()
      });
      
      alert('Leave application submitted successfully.');
      setSelectedEmpId('');
      setReason('');
      setActiveTab('history');
    } catch (e: any) {
      alert(e?.message || 'Failed to apply leave.');
    }
  };

  const handleUpdateStatus = async (id: number, newStatus: 'Approved' | 'Rejected') => {
    if (confirm(`Are you sure you want to ${newStatus.toLowerCase()} this leave application?`)) {
      await db.employeeLeaves.update(id, { status: newStatus });
    }
  };

  return (
    <div className="flex flex-col w-full h-full bg-[#f8faf7] z-20 absolute inset-0 font-sans text-[13px] text-gray-800">
      <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px]">
        <span className="w-1/3 text-left">Leave Management</span>
        <span className="w-1/3 text-center uppercase">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex bg-[#d9e4d5] border-b border-[#1e8585] font-bold">
         <div 
           className={`px-6 py-2 cursor-pointer ${activeTab === 'balances' ? 'bg-white text-[#1e8585] border-t-2 border-[#1e8585]' : 'text-gray-700 hover:bg-white hover:text-[#1e8585]'}`}
           onClick={() => setActiveTab('balances')}
         >
           Leave Balances
         </div>
         <div 
           className={`px-6 py-2 cursor-pointer ${activeTab === 'apply' ? 'bg-white text-[#1e8585] border-t-2 border-[#1e8585]' : 'text-gray-700 hover:bg-white hover:text-[#1e8585]'}`}
           onClick={() => setActiveTab('apply')}
         >
           Apply Leave
         </div>
         <div 
           className={`px-6 py-2 cursor-pointer ${activeTab === 'history' ? 'bg-white text-[#1e8585] border-t-2 border-[#1e8585]' : 'text-gray-700 hover:bg-white hover:text-[#1e8585]'}`}
           onClick={() => setActiveTab('history')}
         >
           Leave History & Approvals
         </div>
      </div>

      <div className="flex-1 p-4 overflow-hidden">
         {activeTab === 'balances' && (
           <div className="bg-white border border-[#1e8585] h-full flex flex-col shadow-sm">
             <div className="flex bg-[#d9e4d5] px-2 py-2 font-bold border-b border-gray-400 text-black">
                <div className="w-[180px]">Employee</div>
                <div className="w-[120px] text-center">Casual (Used)</div>
                <div className="w-[120px] text-center">Sick (Used)</div>
                <div className="w-[120px] text-center">Earned (Used)</div>
                <div className="w-[120px] text-center">LWP (Used)</div>
             </div>
             
             <div className="flex-1 overflow-y-auto">
               {employees?.map((emp, idx) => {
                 const empLeaves = (leaves || []).filter(l => l.employeeId === emp.id && l.status === 'Approved');
                 const casualUsed = empLeaves.filter(l => l.type === 'Casual').length;
                 const sickUsed = empLeaves.filter(l => l.type === 'Sick').length;
                 const earnedUsed = empLeaves.filter(l => l.type === 'Earned').length;
                 const unpaidUsed = empLeaves.filter(l => l.type === 'Unpaid').length;
                 
                 return (
                   <div key={emp.id} className="flex px-2 py-2 border-b border-gray-200 items-center hover:bg-gray-50">
                     <div className="w-[180px] font-bold text-[#1e8585]">{emp.name}</div>
                     <div className="w-[120px] text-center">{casualUsed} days</div>
                     <div className="w-[120px] text-center">{sickUsed} days</div>
                     <div className="w-[120px] text-center">{earnedUsed} days</div>
                     <div className="w-[120px] text-center">{unpaidUsed} days</div>
                   </div>
                 );
               })}
               {!employees?.length && (
                 <div className="p-4 text-center text-gray-500 italic">No active employees found.</div>
               )}
             </div>
           </div>
         )}

         {activeTab === 'apply' && (
           <div className="bg-white border border-[#1e8585] p-6 w-[500px] shadow-md">
              <h2 className="text-[15px] font-bold text-[#1e8585] border-b border-gray-300 pb-2 mb-4">New Leave Application</h2>
              
              <div className="flex flex-col gap-3">
                 <div className="flex flex-col">
                   <label className="font-bold text-gray-700 mb-1">Employee</label>
                   <select 
                     value={selectedEmpId} 
                     onChange={e => setSelectedEmpId(e.target.value === '' ? '' : Number(e.target.value))}
                     className="border border-gray-300 px-2 py-1 outline-none focus:border-[#1e8585]"
                   >
                     <option value="">-- Select Employee --</option>
                     {employees?.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employeeId})</option>)}
                   </select>
                 </div>

                 <div className="flex flex-col">
                   <label className="font-bold text-gray-700 mb-1">Leave Type</label>
                   <select 
                     value={leaveType} 
                     onChange={e => setLeaveType(e.target.value)}
                     className="border border-gray-300 px-2 py-1 outline-none focus:border-[#1e8585]"
                   >
                     <option value="Casual">Casual Leave (CL)</option>
                     <option value="Sick">Sick Leave (SL)</option>
                     <option value="Earned">Earned Leave (EL)</option>
                     <option value="WithoutPay">Leave Without Pay (LWP)</option>
                   </select>
                 </div>

                 <div className="flex gap-4">
                   <div className="flex flex-col flex-1">
                     <label className="font-bold text-gray-700 mb-1">Start Date</label>
                     <input 
                       type="date" 
                       value={startDate} 
                       onChange={e => setStartDate(e.target.value)}
                       className="border border-gray-300 px-2 py-1 outline-none focus:border-[#1e8585]"
                     />
                   </div>
                   <div className="flex flex-col flex-1">
                     <label className="font-bold text-gray-700 mb-1">End Date</label>
                     <input 
                       type="date" 
                       value={endDate} 
                       onChange={e => setEndDate(e.target.value)}
                       className="border border-gray-300 px-2 py-1 outline-none focus:border-[#1e8585]"
                     />
                   </div>
                 </div>

                 <div className="flex flex-col">
                   <label className="font-bold text-gray-700 mb-1">Reason</label>
                   <textarea 
                     value={reason} 
                     onChange={e => setReason(e.target.value)}
                     rows={3}
                     className="border border-gray-300 px-2 py-1 outline-none focus:border-[#1e8585] resize-none"
                     placeholder="State the reason for leave..."
                   />
                 </div>

                 <button 
                   onClick={handleApplyLeave}
                   className="mt-4 bg-[#1e8585] text-white font-bold py-2 hover:bg-[#156666] shadow"
                 >
                   Submit Application
                 </button>
              </div>
           </div>
         )}

         {activeTab === 'history' && (
           <div className="bg-white border border-[#1e8585] h-full flex flex-col shadow-sm">
             <div className="flex bg-[#d9e4d5] px-2 py-2 font-bold border-b border-gray-400 text-black">
                <div className="w-[150px]">Employee</div>
                <div className="w-[100px]">Type</div>
                <div className="w-[180px]">Duration</div>
                <div className="flex-1">Reason</div>
                <div className="w-[100px] text-center">Status</div>
                <div className="w-[160px] text-center">Action</div>
             </div>
             
             <div ref={scrollRef} className="flex-1 overflow-y-auto">
               {flatItems.length === 0 ? (
                 <div className="p-4 text-center text-gray-500 italic">No leave applications found.</div>
               ) : (
                 flatItems.map((leave, idx) => {
                   const emp = employees?.find(e => e.id === leave.employeeId);
                   const isSelected = selectedIndex === idx;
                   return (
                     <div 
                       key={leave.id} 
                       className={`flex px-2 py-2 border-b border-gray-200 items-center ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                       onClick={() => setSelectedIndex(idx)}
                     >
                        <div className="w-[150px] font-bold text-[#1e8585]">{emp?.name || 'Unknown'}</div>
                        <div className="w-[100px]">{leave.type}</div>
                        <div className="w-[180px] text-[12px]">{leave.startDate} to {leave.endDate}</div>
                        <div className="flex-1 truncate pr-4 text-[12px]">{leave.reason}</div>
                        <div className="w-[100px] text-center">
                          <span className={`px-2 py-[2px] rounded text-[11px] font-bold text-white ${leave.status === 'Approved' ? 'bg-green-600' : leave.status === 'Rejected' ? 'bg-red-600' : 'bg-yellow-500'}`}>
                            {leave.status}
                          </span>
                        </div>
                        <div className="w-[160px] flex justify-center gap-2">
                           {leave.status === 'Pending' && (
                             <>
                               <button 
                                 onClick={() => handleUpdateStatus(leave.id!, 'Approved')}
                                 className="px-2 py-1 bg-green-100 text-green-700 border border-green-400 rounded text-[11px] font-bold hover:bg-green-200"
                               >Approve</button>
                               <button 
                                 onClick={() => handleUpdateStatus(leave.id!, 'Rejected')}
                                 className="px-2 py-1 bg-red-100 text-red-700 border border-red-400 rounded text-[11px] font-bold hover:bg-red-200"
                               >Reject</button>
                             </>
                           )}
                        </div>
                     </div>
                   );
                 })
               )}
             </div>
           </div>
         )}
      </div>
    </div>
  );
};
